import { useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import PageShell from "@/components/feature/PageShell";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import Breadcrumb from "@/components/base/Breadcrumb";
import Toast from "@/components/base/Toast";
import { getRelated, getWorkById, getWorkNews, workTypeLabel } from "@/lib/workUtils";
import DetailHeader from "./components/DetailHeader";
import CastSection from "./components/CastSection";
import NewsSection from "./components/NewsSection";
import RelatedSection from "./components/RelatedSection";

export default function DetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const work = useMemo(() => getWorkById(id), [id]);

  const [favorited, setFavorited] = useState(false);
  const [toast, setToast] = useState<{ message: string; icon: string } | null>(null);

  useDocumentMeta({
    title: work ? `${work.title}剧情介绍与详情-妻子的姐姐` : "影视详情-妻子的姐姐",
    description: work
      ? `${work.title}（${work.year}·${work.region}·${workTypeLabel(work.type)}）由${work.director}执导，${work.cast.join("、")}等主演。${work.synopsis.slice(0, 70)}`
      : undefined,
    keywords: work
      ? `妻子的姐姐,${work.title},${work.cast.join(",")},${work.genres.join(",")},剧情介绍,演员信息`
      : undefined,
  });

  const showToast = (message: string, icon = "ri-checkbox-circle-line") => {
    setToast({ message, icon });
  };

  if (!work) {
    return (
      <PageShell>
        <div className="mx-auto flex w-full flex-col items-center justify-center px-4 py-24 text-center md:px-8">
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-200 text-foreground-500">
            <i className="ri-file-unknow-line text-3xl" />
          </span>
          <h1 className="mt-5 font-heading text-xl font-bold text-foreground-950">
            未找到该影视作品
          </h1>
          <p className="mt-2 max-w-md text-sm text-foreground-600">
            你访问的作品可能已被移除或链接有误，可以返回首页或前往分类页继续浏览。
          </p>
          <Link
            to="/"
            className="mt-6 rounded-md bg-accent-500 px-6 py-2.5 text-sm font-semibold text-primary-950 transition-colors hover:bg-accent-600"
          >
            返回首页
          </Link>
        </div>
      </PageShell>
    );
  }

  const related = getRelated(work, 4);
  const news = getWorkNews(work);

  const handleSearchActor = (name: string) => {
    navigate(`/search?q=${encodeURIComponent(name)}`);
  };

  const handleShare = () => {
    const text = `《${work.title}》${work.year} · ${workTypeLabel(work.type)}｜妻子的姐姐影视资料`;
    if (navigator.clipboard) {
      navigator.clipboard.writeText(`${text} ${window.location.href}`).catch(() => undefined);
    }
    showToast("分享链接已复制，快去告诉朋友吧", "ri-share-forward-line");
  };

  return (
    <PageShell>
      <div className="mx-auto w-full px-4 pt-6 md:px-8">
        <Breadcrumb
          items={[
            { label: "首页", to: "/" },
            { label: workTypeLabel(work.type), to: `/category/${work.type}` },
            { label: work.title },
          ]}
        />
      </div>

      <DetailHeader
        work={work}
        favorited={favorited}
        onFavorite={() => {
          setFavorited((v) => !v);
          showToast(favorited ? "已取消收藏" : "已加入收藏（前端演示）", favorited ? "ri-heart-line" : "ri-heart-fill");
        }}
        onShare={handleShare}
        onPlay={() => showToast("播放功能为前端演示，暂不支持真实播放", "ri-play-circle-line")}
        onSearchActor={handleSearchActor}
      />

      <div className="mx-auto grid w-full grid-cols-1 gap-10 px-4 py-10 md:px-8 md:py-14 lg:grid-cols-3">
        <div className="space-y-10 lg:col-span-2">
          <section>
            <h2 className="flex items-center gap-2 font-heading text-xl font-extrabold text-foreground-950 md:text-2xl">
              <span className="h-5 w-1.5 rounded-full bg-accent-500" />
              剧情介绍
            </h2>
            <div className="mt-5 rounded-lg border border-background-200 bg-background-100 p-5 md:p-6">
              <p className="text-sm leading-loose text-foreground-700 md:text-base">
                {work.synopsis}
              </p>
              <p className="mt-4 text-sm leading-loose text-foreground-600">
                《{work.title}》是一部{work.year}年出品的{work.region}
                {workTypeLabel(work.type)}，由{work.director}执导，
                {work.cast.join("、")}等主演，涵盖{work.genres.join("、")}等类型元素。
              </p>
            </div>
          </section>

          <CastSection work={work} onSearchActor={handleSearchActor} />
          <NewsSection items={news} />
          <RelatedSection list={related} />
        </div>

        <aside className="space-y-6">
          <div className="rounded-lg border border-background-200 bg-background-50 p-5">
            <h2 className="font-heading text-base font-bold text-foreground-950">作品速览</h2>
            <dl className="mt-4 space-y-3 text-sm">
              <div className="flex items-center justify-between gap-4">
                <dt className="text-foreground-500">类型</dt>
                <dd className="font-medium text-foreground-800">{workTypeLabel(work.type)}</dd>
              </div>
              <div className="flex items-center justify-between gap-4">
                <dt className="text-foreground-500">上映年份</dt>
                <dd className="font-medium text-foreground-800">{work.year}</dd>
              </div>
              <div className="flex items-center justify-between gap-4">
                <dt className="text-foreground-500">地区</dt>
                <dd className="font-medium text-foreground-800">{work.region}</dd>
              </div>
              <div className="flex items-center justify-between gap-4">
                <dt className="text-foreground-500">时长 / 集数</dt>
                <dd className="font-medium text-foreground-800">
                  {work.type === "tv" ? `${work.episodes} 集` : work.duration}
                </dd>
              </div>
              <div className="flex items-center justify-between gap-4">
                <dt className="text-foreground-500">更新状态</dt>
                <dd className="font-medium text-accent-700">{work.status}</dd>
              </div>
              <div className="flex items-center justify-between gap-4">
                <dt className="text-foreground-500">更新日期</dt>
                <dd className="font-medium text-foreground-800">{work.updatedAt}</dd>
              </div>
            </dl>
            <div className="mt-5 rounded-md bg-accent-50 p-3 text-xs leading-relaxed text-accent-900">
              <i className="ri-information-line mr-1" />
              本站仅提供剧情与演员资料展示，在线播放为前端演示，不含真实播放或下载接口。
            </div>
          </div>

          <div className="rounded-lg border border-background-200 bg-background-100 p-5">
            <h2 className="font-heading text-base font-bold text-foreground-950">猜你想看</h2>
            <ul className="mt-4 space-y-3">
              {related.slice(0, 4).map((item) => (
                <li key={item.id}>
                  <Link
                    to={`/detail/${item.id}`}
                    className="flex items-center gap-3 rounded-md p-2 transition-colors hover:bg-background-50"
                  >
                    <div className="h-14 w-10 shrink-0 overflow-hidden rounded bg-background-200">
                      <img
                        src={item.poster}
                        alt={`${item.title}海报`}
                        title={`${item.title} 剧情介绍`}
                        className="h-full w-full object-cover object-top"
                      />
                    </div>
                    <div className="min-w-0">
                      <p className="line-clamp-1 text-sm font-medium text-foreground-900">
                        {item.title}
                      </p>
                      <p className="mt-0.5 text-xs text-foreground-500">
                        {item.year} · {workTypeLabel(item.type)}
                      </p>
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </aside>
      </div>

      <Toast
        visible={Boolean(toast)}
        message={toast?.message ?? ""}
        icon={toast?.icon}
        onClose={() => setToast(null)}
      />
    </PageShell>
  );
}